import { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    Stack,
    Switch,
    Text,
    Input,
    Select,
    IconButton,
    Button,
    Textarea,
    Spinner, // Added Spinner
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    HStack,
    Icon,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import CloseIcon from '../../custom-components/icons/CloseIcon';
import ZaraButton from '../../custom-components/buttons/Button';

interface EmailConfig {
    type: 'simple' | 'conditional';
    email: string;
    prompt?: string;
    id: number | '';
    send_style?: number;
}

interface EmailNotificationPreferenceProps {
    existingEmailData?: EmailConfig[];
    onSave: (data: { enabled: boolean; emails: EmailConfig[] }) => void;
    emailEnabled?: boolean;
    isLoading?: boolean; // Added isLoading prop
}

const EmailNotificationPreference: React.FC<EmailNotificationPreferenceProps> = ({
    existingEmailData = [],
    onSave,
    emailEnabled = false,
    isLoading = false // Default to false
}) => {
    const [isEnabled, setIsEnabled] = useState(emailEnabled);
    const [emailConfigs, setEmailConfigs] = useState<EmailConfig[]>(
        existingEmailData.length > 0
            ? existingEmailData
            : [{ type: 'simple', email: '', prompt: '', id: '', send_style: 0 }]
    );

    // Sync state with prop changes
    useEffect(() => {
        setIsEnabled(emailEnabled);
    }, [emailEnabled]);

    useEffect(() => {
        setEmailConfigs(
            existingEmailData.length > 0
                ? existingEmailData
                : [{ type: 'simple', email: '', prompt: '', id: '', send_style: 0 }]
        );
    }, [existingEmailData]);

    const handleToggle = () => {
        const newEnabled = !isEnabled;
        setIsEnabled(newEnabled);
        onSave({ enabled: newEnabled, emails: emailConfigs });
    };

    const handleConfigChange = (index: number, field: keyof EmailConfig, value: string) => {
        let newValue = value;
        if (field === 'prompt') {
            const words = value.split(' ').filter(w => w.length > 0);
            if (words.length > 25) {
                newValue = words.slice(0, 25).join(' ');
            }
        }
        const updatedConfigs = [...emailConfigs];
        updatedConfigs[index] = { ...updatedConfigs[index], [field]: newValue };
        setEmailConfigs(updatedConfigs);
    };

    const handleSendStyleChange = (index: number, value: number) => {
        const updatedConfigs = [...emailConfigs];
        updatedConfigs[index] = { ...updatedConfigs[index], send_style: value };
        setEmailConfigs(updatedConfigs);
    };

    const handleTypeChange = (index: number, newType: 'simple' | 'conditional') => {
        const updatedConfigs = [...emailConfigs];
        updatedConfigs[index] = {
            ...updatedConfigs[index],
            type: newType,
            prompt: newType === 'simple' ? '' : updatedConfigs[index].prompt || ''
        };
        setEmailConfigs(updatedConfigs);
    };

    const addEmailConfig = () => {
        setEmailConfigs([...emailConfigs, { type: 'simple', email: '', prompt: '', id: '', send_style: 0 }]);
    };

    const removeEmailConfig = (index: number) => {
        if (emailConfigs.length > 1) {
            const updatedConfigs = emailConfigs.filter((_, i) => i !== index);
            setEmailConfigs(updatedConfigs);
        }
    };

    const handleSave = () => {
        onSave({ enabled: isEnabled, emails: emailConfigs });
    };

    const getTypeLabel = (type: string) => {
        return type === 'simple' ? 'Simple' : 'Conditional';
    };

    const getTypeIcon = (type: string) => {
        return type === 'simple' ? '📧' : '⚡';
    };

    return (
        <Box position="relative">
            {/* Overlay spinner when loading */}
            {isLoading && (
                <Flex position="absolute" top={0} left={0} right={0} bottom={0} zIndex={10} align="center" justify="center" bg="whiteAlpha.700">
                    <Spinner size="xl" thickness="4px" color="gray.400" />
                </Flex>
            )}
            <Box pointerEvents={isLoading ? 'none' : 'auto'} opacity={isLoading ? 0.6 : 1}>
                <Flex
                    fontSize={{ base: '24px', sm: '28px' }}
                    lineHeight='28px'
                    fontWeight={{ base: '400', sm: '700' }}
                    textAlign='left'
                    gap={{ base: '10px', sm: '10px' }}
                    alignItems='center'
                    mb='10px'
                >
                    <Switch
                        isChecked={isEnabled}
                        onChange={handleToggle}
                        colorScheme='blackAlpha'
                        isDisabled={isLoading}
                    />
                    <Text fontSize='md'>Email Notifications</Text>
                </Flex>

                {isEnabled && (
                    <Box
                        border='1px solid'
                        borderColor='gray.200'
                        borderRadius='md'
                        p='4'
                    >
                        <Stack spacing='4'>
                            <Box>
                                <Text fontSize='lg' fontWeight='600' mb='2'>
                                    Email Setup
                                </Text>
                                <Text fontSize='sm' color='gray.600' mb='4'>
                                    📧 {emailConfigs.filter(c => c.type === 'simple').length} Simple •
                                    ⚡ {emailConfigs.filter(c => c.type === 'conditional').length} Conditional
                                </Text>
                            </Box>

                            {emailConfigs.map((config, index) => (
                                <Box
                                    key={index}
                                    border='1px solid'
                                    borderColor='gray.200'
                                    borderRadius='md'
                                    p='4'
                                    bg='gray.50'
                                >
                                    <Flex justify='space-between' align='center' mb='4'>
                                        <Flex align='center' gap='2'>
                                            <Text fontSize='sm' fontWeight='600'>
                                                {getTypeIcon(config.type)} {getTypeLabel(config.type)}
                                            </Text>
                                            <Text fontSize='sm' color='gray.600'>
                                                Email {index + 1}
                                            </Text>
                                        </Flex>
                                        <Flex align='center' gap='2'>
                                            <Select
                                                size='sm'
                                                value={config.type}
                                                onChange={(e) => handleTypeChange(index, e.target.value as 'simple' | 'conditional')}
                                                bg='white'
                                                w='120px'
                                                isDisabled={isLoading}
                                            >
                                                <option value='simple'>Simple</option>
                                                <option value='conditional'>Conditional</option>
                                            </Select>
                                            {emailConfigs.length > 1 && (
                                                <IconButton
                                                    size='sm'
                                                    onClick={() => removeEmailConfig(index)}
                                                    aria-label='Remove email'
                                                    icon={<CloseIcon />}
                                                    variant='ghost'
                                                    isDisabled={isLoading}
                                                />
                                            )}
                                        </Flex>
                                    </Flex>

                                    {config.type === 'conditional' && (
                                        <Box mb='4'>
                                            <Text fontSize='sm' fontWeight='600' mb='2'>
                                                If the caller... (max 25 words)
                                            </Text>
                                            <Textarea
                                                placeholder='e.g., mentions urgent, says emergency, asks for manager'
                                                value={config.prompt || ''}
                                                onChange={(e) => handleConfigChange(index, 'prompt', e.target.value)}
                                                size='sm'
                                                resize='none'
                                                rows={2}
                                                isDisabled={isLoading}
                                            />
                                            <Text fontSize='xs' color='gray.500' mt='1'>
                                                Words: {(config.prompt || '').split(' ').filter(w => w.length > 0).length}/25
                                            </Text>
                                        </Box>
                                    )}

                                    <Box>
                                        <Text fontSize='sm' fontWeight='600' mb='2'>
                                            {config.type === 'simple' ? 'Send to email address:' : 'Then send email to:'}
                                        </Text>
                                        <Text fontSize='xs' color='gray.600' mb='2'>
                                            Frequency
                                        </Text>
                                        {/* Dropdown implemented with Chakra Menu so it opens on click */}
                                        <Box mb='2' w='100%'>
                                            <Menu>
                                                <MenuButton as={Button} variant='outline' w='100%' textAlign='left'>
                                                    <HStack spacing={3} align='center'>
                                                        {config.send_style === 0 ? <Text fontSize='lg'>⚡</Text> : config.send_style === 1 ? <Text fontSize='lg'>📅</Text> : config.send_style === 2 ? <Text fontSize='lg'>📆</Text> : <Text fontSize='lg'>📊</Text>}
                                                        <Box textAlign='left'>
                                                            <Text fontSize='sm' fontWeight='500'>
                                                                {config.send_style === 0 ? 'Immediate' : config.send_style === 1 ? 'Daily' : config.send_style === 2 ? 'Weekly' : 'Monthly'}
                                                            </Text>
                                                            <Text fontSize='xs' color='gray.500'>
                                                                {config.send_style === 0 ? 'Get notified instantly via email.' : config.send_style === 1 ? 'A summary digest every morning at 8 AM.' : config.send_style === 2 ? 'A weekly report sent every Monday.' : 'A monthly report sent on the 1st.'}
                                                            </Text>
                                                        </Box>
                                                    </HStack>
                                                </MenuButton>
                                                <MenuList minW='320px' w='auto'>
                                                    {[
                                                        { value: 0, label: 'Immediate', description: 'Get notified instantly via email.', icon: '⚡' },
                                                        { value: 1, label: 'Daily', description: 'A summary digest every morning at 8 AM.', icon: '📅' },
                                                        { value: 2, label: 'Weekly', description: 'A weekly report sent every Monday.', icon: '📆' },
                                                        { value: 3, label: 'Monthly', description: 'A monthly report sent on the 1st.', icon: '📊' },
                                                    ].map((opt) => (
                                                        <MenuItem
                                                            key={opt.value}
                                                            onClick={() => handleSendStyleChange(index, opt.value)}
                                                            _hover={{ bg: 'gray.50' }}
                                                            bg={config.send_style === opt.value ? 'blue.50' : undefined}
                                                        >
                                                            <HStack spacing={3} flex='1' align='center'>
                                                                <Text fontSize='lg'>{opt.icon}</Text>
                                                                <Box textAlign='left'>
                                                                    <Text fontWeight={config.send_style === opt.value ? '600' : '500'}>{opt.label}</Text>
                                                                    <Text fontSize='xs' color='gray.500'>{opt.description}</Text>
                                                                </Box>
                                                            </HStack>
                                                            {config.send_style === opt.value && <Text color='blue.500'>✓</Text>}
                                                        </MenuItem>
                                                    ))}
                                                </MenuList>
                                            </Menu>
                                        </Box>
                                        <Input
                                            placeholder='Enter email address'
                                            value={config.email}
                                            onChange={(e) => handleConfigChange(index, 'email', e.target.value)}
                                            size='sm'
                                            isDisabled={isLoading}
                                        />
                                        <Text fontSize='xs' color='gray.500' mt='2'>
                                            {config.type === 'simple'
                                                ? 'This email will receive notifications for all calls.'
                                                : 'This email will only receive notifications when the caller [condition].'}
                                        </Text>
                                    </Box>
                                </Box>
                            ))}

                            <Button
                                onClick={addEmailConfig}
                                variant='outline'
                                size='sm'
                                leftIcon={<FontAwesomeIcon icon={faPlus} />}
                                alignSelf='flex-start'
                                isDisabled={isLoading}
                            >
                                Add email
                            </Button>

                            <Box>
                                <Text fontSize='sm' fontWeight='600' mb='2'>Summary:</Text>
                                <Stack spacing='1' fontSize='sm' color='gray.600'>
                                    <Text>

                                        • {(() => {
                                            const count = emailConfigs.filter((c: EmailConfig) => c.type === 'simple').length;
                                            return `${count} ${count === 1 ? 'email' : 'emails'} will receive all notifications`;
                                        })()}
                                    </Text>
                                    <Text>

                                        • {(() => {
                                            const count = emailConfigs.filter((c: EmailConfig) => c.type === 'conditional').length;
                                            return `${count} ${count === 1 ? 'email' : 'emails'} will receive conditional notifications`;
                                        })()}
                                    </Text>
                                </Stack>
                            </Box>

                            <ZaraButton
                                onClick={handleSave}
                                w='auto'
                                fontSize='sm'
                                alignSelf='flex-start'
                                variant={'primary'}
                                isDisabled={isLoading}
                            >
                                Save
                            </ZaraButton>
                        </Stack>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default EmailNotificationPreference;
